/**
 * FeedbackPanel Component
 * Allows users to rate and provide feedback on assistant responses.
 * Demo/mock only — feedback is stored in component state, not persisted.
 */

import { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  Collapse,
  Chip,
} from '@mui/material';
import {
  ThumbUp,
  ThumbUpOutlined,
  ThumbDown,
  ThumbDownOutlined,
  CheckCircle,
} from '@mui/icons-material';

const ISSUE_OPTIONS = [
  'Needs Context',
  'Verify Data',
  'Not Relevant',
  'Hard to Understand',
  'Too Subjective',
  'Other',
];

export default function FeedbackPanel({ messageId }) {
  const [sentiment, setSentiment] = useState(null); // 'up' | 'down' | null
  const [issues, setIssues] = useState([]);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showUpComment, setShowUpComment] = useState(false);

  const handleSentiment = (value) => {
    if (submitted) return;
    const newValue = sentiment === value ? null : value;
    setSentiment(newValue);
    // Thumbs up with no prior interaction — submit immediately
    if (newValue === 'up' && !comment.trim()) {
      const feedback = {
        messageId,
        sentiment: 'up',
        timestamp: new Date().toISOString(),
      };
      console.log('[FeedbackPanel] Submitted feedback:', feedback);
      setSubmitted(true);
    }
  };

  const toggleIssue = (issue) => {
    setIssues((prev) =>
      prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
    );
  };

  const handleSubmit = () => {
    const feedback = {
      messageId,
      sentiment,
      ...(comment.trim() && { comment: comment.trim() }),
      ...(sentiment === 'down' && issues.length > 0 && { issues }),
      timestamp: new Date().toISOString(),
    };
    console.log('[FeedbackPanel] Submitted feedback:', feedback);
    setSubmitted(true);
    setShowUpComment(false);
  };

  const handleReset = () => {
    setSentiment(null);
    setIssues([]);
    setComment('');
    setSubmitted(false);
    setShowUpComment(false);
  };

  if (submitted) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CheckCircle sx={{ fontSize: 16, color: sentiment === 'up' ? 'success.main' : 'error.main' }} />
        <Typography variant="caption" color="text.secondary">
          Feedback recorded
        </Typography>
        {sentiment === 'up' && !comment.trim() && (
          <Chip label="Add comment" size="small" variant="outlined" onClick={() => { setSubmitted(false); setShowUpComment(true); }} sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        <Chip label="Edit" size="small" variant="outlined" onClick={handleReset} sx={{ height: 20, fontSize: '0.7rem' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, borderTop: '1px solid #eee', pt: 1 }}>
      {/* Thumbs up / down */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => handleSentiment('up')} color={sentiment === 'up' ? 'success' : 'default'}>
          {sentiment === 'up' ? <ThumbUp fontSize="small" /> : <ThumbUpOutlined fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={() => handleSentiment('down')} color={sentiment === 'down' ? 'error' : 'default'}>
          {sentiment === 'down' ? <ThumbDown fontSize="small" /> : <ThumbDownOutlined fontSize="small" />}
        </IconButton>

      </Box>

      {/* Issue flags + comments shown on thumbs down; optional comments on thumbs up via Edit */}
      <Collapse in={sentiment === 'down'}>
        <Box sx={{ pl: 1, pr: 1, pb: 1, pt: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
            Flag issues (select all that apply)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            {ISSUE_OPTIONS.map((issue) => (
              <Chip
                key={issue}
                label={issue}
                size="small"
                variant={issues.includes(issue) ? 'filled' : 'outlined'}
                color={issues.includes(issue) ? 'error' : 'default'}
                onClick={() => toggleIssue(issue)}
                sx={{ fontSize: '0.78rem' }}
              />
            ))}
          </Box>

          <TextField
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            placeholder="Additional comments (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button size="small" variant="contained" onClick={handleSubmit} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Submit Feedback
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Optional comment for thumbs up */}
      <Collapse in={showUpComment && sentiment === 'up'}>
        <Box sx={{ pl: 1, pr: 1, pb: 1, pt: 1 }}>
          <TextField
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            placeholder="Comments (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button size="small" variant="contained" onClick={handleSubmit} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Submit
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
